import { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import { fmtMoney, fmtDate, todayISO } from "@/lib/utils-format";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const METHODS = ["Cash", "Zelle", "Check", "Credit/Debit Card", "ACH Transfer"];

export default function Payments() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    const [a,b,c] = await Promise.all([endpoints.list("payments"), endpoints.list("clients"), endpoints.list("invoices")]);
    setItems(a.data); setClients(b.data); setInvoices(c.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing(null);
    setForm({ client_id:"", client_name:"", invoice_number:"", amount:0, method:"Cash", date: todayISO(), status:"Received", notes:"" });
    setOpen(true);
  };
  const startEdit = (p) => { setEditing(p); setForm(p); setOpen(true); };

  const save = async () => {
    if (editing) await endpoints.update("payments", editing.id, form);
    else await endpoints.create("payments", form);
    toast.success("Payment recorded"); setOpen(false); load();
  };
  const remove = async (id) => { if(!window.confirm("Delete?"))return; await endpoints.remove("payments", id); load(); };

  const total = items.reduce((s,p)=> s + Number(p.amount||0), 0);
  const invoiceOptions = useMemo(
    () => invoices.filter(i => !form.client_id || i.client_id === form.client_id),
    [invoices, form.client_id]
  );

  return (
    <Layout title="Payments" subtitle={`Total received: ${fmtMoney(total)}`}>
      <div className="flex justify-end mb-4"><Button onClick={startNew} data-testid="payments-new"><Plus className="size-4 mr-1.5"/>Record Payment</Button></div>
      <Card className="rounded-xl overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="payments-table">
          <thead className="bg-muted/40"><tr className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <th className="p-4 font-semibold">Date</th><th className="p-4 font-semibold">Customer</th>
            <th className="p-4 font-semibold">Invoice</th><th className="p-4 font-semibold">Method</th>
            <th className="p-4 font-semibold">Amount</th><th className="p-4 font-semibold">Status</th><th className="p-4 w-20"></th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-muted-foreground">No payments yet.</td></tr>}
            {items.map(p => (
              <tr key={p.id} className="border-t border-border hover:bg-muted/30">
                <td className="p-4">{fmtDate(p.date)}</td>
                <td className="p-4">{p.client_name}</td>
                <td className="p-4 font-mono text-xs">{p.invoice_number}</td>
                <td className="p-4">{p.method}</td>
                <td className="p-4 font-mono font-semibold text-secondary">{fmtMoney(p.amount)}</td>
                <td className="p-4"><StatusPill status={p.status}/></td>
                <td className="p-4">
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="size-7" onClick={()=>startEdit(p)}><Pencil className="size-3.5"/></Button>
                    <Button size="icon" variant="ghost" className="size-7 text-destructive" onClick={()=>remove(p.id)}><Trash2 className="size-3.5"/></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Payment" : "Record Payment"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5 col-span-2"><Label>Customer</Label>
              <Select value={form.client_id||""} onValueChange={(id)=>{const c = clients.find(x=>x.id===id); setForm({...form, client_id:id, client_name: c?`${c.first_name} ${c.last_name}`:""});}}>
                <SelectTrigger><SelectValue placeholder="Select client"/></SelectTrigger>
                <SelectContent>{clients.map(c=><SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Invoice #</Label>
              <Select value={form.invoice_number||""} onValueChange={(v)=>setForm({...form, invoice_number:v})}>
                <SelectTrigger><SelectValue placeholder="Optional"/></SelectTrigger>
                <SelectContent>{invoiceOptions.map(i=><SelectItem key={i.id} value={i.number}>{i.number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Amount</Label><Input type="number" step="0.01" value={form.amount||0} onChange={(e)=>setForm({...form, amount:Number(e.target.value)})}/></div>
            <div className="space-y-1.5"><Label>Method</Label>
              <Select value={form.method||"Cash"} onValueChange={(v)=>setForm({...form, method:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{METHODS.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date||""} onChange={(e)=>setForm({...form, date:e.target.value})}/></div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes||""} onChange={(e)=>setForm({...form, notes:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="payment-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
